import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { usePackingListStore } from 'stores/packingList/packingList';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { cn } from 'support/helpers/cn/cn';
import { DocumentTypeWithVirtual, PartnerV2 } from 'support/types';
import { Alert, AlertSection } from 'components/Display/Alert';
import { Popup } from 'components/Display/Popup/Popup';
import { Button } from 'components/Form/Button';
import { Translate } from 'components/Translate/Translate';
import { useDocumentDisplayConfig } from 'components/WebEDI/DocumentDisplayConfig/DocumentDisplayConfigContext';
import { CollapsibleSummaryRow } from 'components/WebEDI/DocumentSummary/components/CollapsibleSummaryRow/CollapsibleSummaryRow';
import { DocumentSummary } from 'components/WebEDI/DocumentSummary/DocumentSummary';
import { filterSummaryByConfig } from 'components/WebEDI/DocumentSummary/filterSummaryByConfig';
import { Summary } from 'components/WebEDI/DocumentSummary/types';

const PACKING_LIST_DOCUMENT_TYPES: Array<DocumentTypeWithVirtual> = [
  DocumentTypeWithVirtual.shippingNotice,
  DocumentTypeWithVirtual.dispatchResponse,
];

type SendConfirmationModalProps = {
  onConfirm: () => void;
  onCancel: () => void;
  open: boolean;
  documentType: DocumentTypeWithVirtual;
  receiverTradePartner: Pick<PartnerV2, 'name'> | undefined;
  showSummary?: boolean;
};
export const SendConfirmationModal: FC<SendConfirmationModalProps> = ({
  onConfirm,
  onCancel,
  open,
  documentType,
  receiverTradePartner,
  showSummary = true,
}) => {
  const { t } = useTranslation();
  const documentTypeTranslation = t(`common:messageTypes.singular.${documentType}`);
  const isBig = useWebEDIStore((state) => state.isBigMessage);
  const packingListEnabled = usePackingListStore((state) => state.enabled);
  // Both shipment documents can leave line items outside a transport unit, and those are dropped
  // from the payload — the packing-list variant is the only place that says so before sending.
  const descriptionKey =
    packingListEnabled && PACKING_LIST_DOCUMENT_TYPES.includes(documentType)
      ? `webedi:sendDocumentConfirmation.description.${documentType}_PACKING_LIST`
      : `webedi:sendDocumentConfirmation.description.${documentType}`;

  const CancelButton = () => (
    <Button analyticsId="webedi:cancelSendDialog" onClick={onCancel} variant="secondary">
      <Translate i18nKey="common:cancel" />
    </Button>
  );

  const PrimaryButton = () => (
    <Button analyticsId="webedi:confirmSendDialog" onClick={onConfirm}>
      {documentType === DocumentTypeWithVirtual.partialOrderConfirmation ? (
        <Translate i18nKey="webedi:sendDocumentConfirmation.partialOrderResponse.primaryButton" />
      ) : (
        <Translate
          i18nKey="webedi:sendDocumentConfirmation.primaryButton"
          values={{ documentType: documentTypeTranslation }}
        />
      )}
    </Button>
  );

  return (
    <Popup
      CancelButton={CancelButton}
      PrimaryButton={PrimaryButton}
      open={open}
      onClose={onCancel}
      title={
        documentType === DocumentTypeWithVirtual.partialOrderConfirmation ? (
          <Translate i18nKey="webedi:sendDocumentConfirmation.partialOrderResponse.title" />
        ) : (
          <Translate
            i18nKey="webedi:sendDocumentConfirmation.title"
            values={{ documentType: documentTypeTranslation }}
          />
        )
      }
      subtitle={
        showSummary ? (
          <Translate
            i18nKey={descriptionKey}
            values={{ documentType: documentTypeTranslation, receiverPartner: receiverTradePartner?.name }}
          />
        ) : (
          <Translate
            i18nKey="webedi:sendDocumentConfirmation.descriptionWithoutSummary"
            values={{ documentType: documentTypeTranslation.toLowerCase() }}
          />
        )
      }
    >
      {showSummary ? <DocumentSummary documentType={documentType} renderSummary={SummaryRenderer} /> : null}
      {isBig ? (
        <Alert severity="warning">
          <AlertSection severity="warning" title={t('webedi:sendDocumentConfirmation.bigMessage.title')}>
            <Translate i18nKey="webedi:sendDocumentConfirmation.bigMessage.description" />
          </AlertSection>
        </Alert>
      ) : null}
    </Popup>
  );
};

const SummaryRenderer = ({ summary }: { summary: Summary }) => {
  const config = useDocumentDisplayConfig();
  const filtered = filterSummaryByConfig(summary, config);

  if (filtered.content.length === 0) {
    return null;
  }

  return (
    <div className="my-4 flex flex-col gap-4 rounded-md bg-gray-50 p-4">
      {filtered.content.map((items) =>
        items
          .filter((item) => (item.test ? item.test('send-confirmation-modal') : true))
          .map((item) =>
            item.subItems?.length ? (
              <CollapsibleSummaryRow
                key={item.label}
                label={item.label}
                value={item.value}
                subItems={item.subItems}
                className={cn(
                  'flex justify-between text-sm',
                  item.emphasis === 'warning' ? 'text-orange-600' : 'text-gray-500',
                )}
                labelClassName="shrink-0"
                valueClassName={cn('text-right', item.emphasis === 'warning' ? 'text-orange-600' : 'text-black')}
              />
            ) : (
              <p
                key={item.label}
                className={cn(
                  'flex items-start justify-between gap-3 text-sm',
                  item.emphasis === 'warning' ? 'text-orange-600' : 'text-gray-500',
                )}
              >
                <span className="shrink-0">{item.label}</span>
                <span className={cn('text-right', item.emphasis === 'warning' ? 'text-orange-600' : 'text-black')}>
                  {item.value}
                </span>
              </p>
            ),
          ),
      )}
    </div>
  );
};

/**
 * i18n
 * t('webedi:sendDocumentConfirmation.description.ORDER')
 * t('webedi:sendDocumentConfirmation.description.ORDER_RESPONSE')
 * t('webedi:sendDocumentConfirmation.description.SHIPPING_NOTICE')
 * t('webedi:sendDocumentConfirmation.description.SHIPPING_NOTICE_PACKING_LIST')
 * t('webedi:sendDocumentConfirmation.description.INVOICE')
 * t('webedi:sendDocumentConfirmation.description.CREDIT_NOTE')
 * t('webedi:sendDocumentConfirmation.description.PRODUCT_CATALOG')
 * t('webedi:sendDocumentConfirmation.description.PARTIAL_ORDER_CONFIRMATION')
 * t('webedi:sendDocumentConfirmation.description.ORDER_CONFIRMATION')
 * t('webedi:sendDocumentConfirmation.description.DISPATCH_INSTRUCTION_RESPONSE')
 * t('webedi:sendDocumentConfirmation.description.DISPATCH_INSTRUCTION_RESPONSE_PACKING_LIST')
 * t('webedi:sendDocumentConfirmation.descriptionWithoutSummary')
 */
